import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition,
} from "@headlessui/react";
import clsx from "clsx";
import { FileEdit, Link, MoreVertical, Unlink } from "lucide-react";
import { IconButton } from "@/components/Button/IconButton";
import { AppText } from "@/components/Text/AppText";
import { IUserJob } from "@/types";

interface UserJobButtonProps {
    job: IUserJob;
    isActive: boolean;
    setLinkedJobId: () => void;
    onEditJob: () => void;
    activeClassName?: string;
}

export const UserJobButton = ({
    job,
    isActive,
    setLinkedJobId,
    onEditJob,
    activeClassName = "bg-neutral-200",
}: UserJobButtonProps) => {
    return (
        <Menu
            as="div"
            className="relative inline-block text-left"
        >
            {({ open }) => (
                <>
                    <MenuButton as="div">
                        <div
                            className={clsx(
                                "flex cursor-pointer items-center justify-between gap-2 rounded-lg border border-neutral-300 p-2 hover:bg-neutral-100",
                                isActive && activeClassName,
                            )}
                        >
                            <div className="flex flex-col">
                                <AppText
                                    variant="labelsbuttons"
                                    className="line-clamp-1 text-neutral-900"
                                >
                                    {job.jobTitle}
                                </AppText>
                                {job.companyName && (
                                    <AppText
                                        variant="footnote"
                                        className="line-clamp-1 font-semibold text-neutral-700"
                                    >
                                        {job.companyName}
                                    </AppText>
                                )}
                            </div>
                            <IconButton
                                color={"grey"}
                                aria-label="More actions"
                                className="block w-5 flex-shrink-0"
                            >
                                <MoreVertical size={16} />
                            </IconButton>
                        </div>
                    </MenuButton>
                    <Transition
                        show={open}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            modal={true}
                            anchor={{ to: "top end", gap: 8 }}
                            className="z-50 flex w-40 transform flex-col overflow-hidden rounded-lg bg-neutral-50 shadow-lg ring-2 ring-neutral-300 focus:outline-none"
                        >
                            <MenuItem
                                as="button"
                                onClick={setLinkedJobId}
                                className="flex h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop"
                            >
                                {isActive ? (
                                    <Unlink className="h-5 w-5" />
                                ) : (
                                    <Link className="h-5 w-5" />
                                )}
                                {isActive ? "Detach Job" : "Attach Job"}
                            </MenuItem>
                            <MenuItem
                                as="button"
                                onClick={onEditJob}
                                className="hidden h-10 w-full cursor-pointer items-center gap-2 border-b-2 border-neutral-300 px-3 text-app-labelsbuttons-4-mobile hover:bg-neutral-100 md:text-app-labelsbuttons-4-desktop lg:flex"
                            >
                                <FileEdit className="h-5 w-5" />
                                Edit
                            </MenuItem>
                        </MenuItems>
                    </Transition>
                </>
            )}
        </Menu>
    );
};
